"use client"

import { createContext, useContext, ReactNode } from "react"
import { useQuery } from "@tanstack/react-query"
import { Account } from "@/components/e2e/types"

interface AccountsContextType {
  accounts: Account[]
  loading: boolean
  error: string | null
  refreshAccounts: () => Promise<void>
}

const AccountsContext = createContext<AccountsContextType | undefined>(undefined)

/**
 * Backed by the shared QueryClient — the accounts list is fetched once and
 * served from cache on every SPA navigation for 5 minutes, instead of a raw
 * fetch on each mount. Same queryKey as app/watcher's useAccounts so both
 * surfaces share one cache entry.
 */
export function AccountsProvider({ children }: { children: ReactNode }) {
  const { data, isPending, error, refetch } = useQuery<Account[], Error>({
    queryKey: ["accounts"],
    queryFn: async () => {
      const response = await fetch("/api/accounts")
      if (!response.ok) {
        throw new Error("Failed to fetch accounts")
      }
      return response.json()
    },
    staleTime: 5 * 60 * 1000,
  })

  const refreshAccounts = async () => {
    await refetch()
  }

  return (
    <AccountsContext.Provider
      value={{
        accounts: data ?? [],
        loading: isPending,
        error: error ? error.message : null,
        refreshAccounts,
      }}
    >
      {children}
    </AccountsContext.Provider>
  )
}

export function useAccounts() {
  const context = useContext(AccountsContext)
  if (context === undefined) {
    throw new Error("useAccounts must be used within an AccountsProvider")
  }
  return context
}
